<template>
  <div class="grid-container grid-padding-x">
    <div class="grid-x service-status align-center">
      <div class="cell">
        <div class="date-divider">
          <h2 class="date">
            <span class="show-for-sr">Service updates on</span>{{ formattedDate }}
          </h2>
        </div>
      </div>
      <div
        v-show="loading"
        class="mtm center"
      >
        <i class="fas fa-spinner fa-spin fa-3x" />
      </div>
      <div
        v-show="!loading"
        class="grid-container service-updates grid-x grid-padding-x"
      >
        <!-- If there's a delay in trash, display Trash first -->
        <template v-if="trashUpdate.delay || trashUpdate.undetermined">
          <div class="update-1 trash-recycling cell auto small-24 medium-12">
            <div class="header">
              <i
                :class="{
                  'fa-solid fa-circle-exclamation': trashUpdate.delay || trashUpdate.undetermined,
                  'fa-solid fa-circle-check': !trashUpdate.delay && !trashUpdate.undetermined
                }"
                :style="{ color: (trashUpdate.delay || trashUpdate.undetermined) ? 'red' : 'green' }"
              />
              Trash and recycling
            </div>
            <div 
              v-if="todayHoliday && !trashUpdate.undetermined"
              class="content"
            >
              <div
                class="content"
                v-html="trashUpdate.status"
              />
              <a href="https://www.phila.gov/trashday">Check the holiday schedule.</a>
            </div>
            <div 
              v-else
              class="content"
            >
              <div
                class="content"
                v-html="trashUpdate.status"
              />
            </div>
          </div>
          <div class="update-2 city-offices cell auto small-24 medium-12">
            <div class="header">
              <i
                :class="serviceUpdate.icon"
                :style="{ color: serviceUpdate.color }"
              />
              City offices
            </div>
            <span class="content">
              {{ serviceUpdate.message }}
            </span>
            <span 
              v-if="serviceUpdate.link"
              class="content"
            >
              <a :href="serviceUpdate.link">{{ serviceUpdate.link_text == null ? serviceUpdate.link : serviceUpdate.link_text }}</a>
            </span>
          </div>
        </template>

        <!-- Otherwise, display City Offices first -->
        <template v-else>
          <div class="update-1 city-offices cell auto small-24 medium-12">
            <div class="header">
              <i
                :class="serviceUpdate.icon"
                :style="{ color: serviceUpdate.color }"
              />
              City offices
            </div>
            <span class="content">
              {{ serviceUpdate.message }}
            </span>
            <span 
              v-if="serviceUpdate.link"
              class="content"
            >
              <a :href="serviceUpdate.link">{{ serviceUpdate.link_text == null ? serviceUpdate.link : serviceUpdate.link_text }}</a>
            </span>
          </div>
          <div class="update-2 trash-recycling cell auto small-24 medium-12">
            <div class="header">
              <i
                :class="{
                  'fa-solid fa-circle-exclamation': trashUpdate.delay || trashUpdate.undetermined,
                  'fa-solid fa-circle-check': !trashUpdate.delay && !trashUpdate.undetermined
                }"
                :style="{ color: (trashUpdate.delay || trashUpdate.undetermined) ? 'red' : 'green' }"
              />
              Trash & recycling
            </div>
            <div 
              v-if="todayHoliday && !trashUpdate.undetermined"
              class="content"
            >
              <div
                class="content"
                v-html="trashUpdate.status"
              />
              <a href="https://www.phila.gov/trashday">Check the holiday schedule.</a>
            </div>
            <div 
              v-else
              class="content"
            >
              <div
                class="content"
                v-html="trashUpdate.status"
              /> <a href="https://www.phila.gov/trashday">Find your collection day.</a>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>


<script>
import axios from "axios";
// import moment from "moment";

export default {
  data() {
    return {
      currentDate: new Date(),
      trashAPIUrl: process.env.VUE_APP_CLOSURE_API_URL,
      serviceAPIUrl: process.env.VUE_APP_SERVICE_UPDATES_API_URL,
      trashUpdate: {
        undetermined: null,
        delay: null,
        status: null,
      },
      serviceUpdate: {
        start_date: null,
        end_date: null,
        start_date_time: null,
        end_date_time: null,
        message: null,
        link: null,
        link_text: null,
        color: null,
        icon: null,
      },
      todayHoliday: null,
      loading: true,
    };
  },
  computed: {
    formattedDate() {
      return this.currentDate.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });
    },
  },
  mounted() {
    this.loading = true;
    this.getTrashUpdate();
    this.setServiceUpdate();
    this.checkIfTodayIsHoliday();
  },
  methods: {
    async isToday() {
      const response = await axios.get(this.trashAPIUrl);
      const holidays = response.data["holidays"];

      const today = new Date();
      today.setHours(0, 0, 0, 0); // local midnight

      const holidayToday = holidays.find(holiday => {
        const [ year, month, day ] = holiday.start_date.split("-").map(Number);
        const holidayDate = new Date(year, month - 1, day); 
        holidayDate.setHours(0, 0, 0, 0); // make sure it’s midnight local time

        return today.getTime() === holidayDate.getTime();
      });

      return holidayToday;
    },
    async checkIfTodayIsHoliday() {
      this.todayHoliday = await this.isToday();
    },
    async getTrashUpdate() {
      try {
        const response = await axios.get(this.trashAPIUrl);
        this.trashUpdate.undetermined = response.data["undetermined"];
        this.trashUpdate.delay = response.data["delay"];
        this.trashUpdate.status = this.decodeHtml(response.data["status"]);
      } catch (error) {
        console.error(error);
      }
    },
    decodeHtml(html) {
      const textArea = document.createElement('textarea');
      textArea.innerHTML = html;
      return textArea.value;
    },
    async getServiceUpdate() {
      const response = await axios.get(this.serviceAPIUrl);
      this.serviceUpdate = response.data.filter(update => update['meta_box']['service_update']['phila_update_type'] === "city");
      return this.serviceUpdate.filter((update) => {
        if (update['meta_box']['service_update']['phila_date_format'] == 'none') {
          return true;
        }
        if (update['meta_box']['service_update']['phila_date_format'] == 'date') {
          const currentTime = new Date();
          const options = {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour12: false,
          };
          const formattedCurrentTimeString = currentTime.toLocaleDateString('en-US', options);
          const formattedCurrentTime = new Date(formattedCurrentTimeString);
          options.timeZone = "UTC";
          const dateObj = new Date(update['meta_box']['service_update']['phila_effective_start_date']['timestamp'] * 1000);
          const alertStartTimeString = dateObj.toLocaleDateString('en-US', options);
          const alertStartTime = new Date(alertStartTimeString);
          const dateObj2 = new Date(update['meta_box']['service_update']['phila_effective_end_date']['timestamp'] * 1000);
          const alertEndTimeString = dateObj2.toLocaleDateString('en-US', options);
          const alertEndTime = new Date(alertEndTimeString);
          if (formattedCurrentTime >= alertStartTime && formattedCurrentTime <= alertEndTime) {
            return true;
          }
        }
        if (update['meta_box']['service_update']['phila_date_format'] == 'datetime') {
          this.serviceUpdate.start_date_time = update['meta_box']['service_update']['phila_effective_start_datetime']['timestamp'];
          this.serviceUpdate.end_date_time = update['meta_box']['service_update']['phila_effective_end_datetime']['timestamp'];
          const currentTime = new Date();
          const options = {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: false,
          };
          const formattedCurrentTimeString = currentTime.toLocaleDateString('en-US', options);
          const formattedCurrentTime = new Date(formattedCurrentTimeString);
          options.timeZone = "UTC";
          const dateObj = new Date(this.serviceUpdate.start_date_time * 1000);
          const alertStartTimeString = dateObj.toLocaleDateString('en-US', options);
          const alertStartTime = new Date(alertStartTimeString);
          const dateObj2 = new Date(this.serviceUpdate.end_date_time * 1000);
          const alertEndTimeString = dateObj2.toLocaleDateString('en-US', options);
          const alertEndTime = new Date(alertEndTimeString);
          if (formattedCurrentTime >= alertStartTime && formattedCurrentTime <= alertEndTime) {
            return true;
          }
        }
      });
    },
    async setServiceUpdate() {
      const serviceUpdate = await this.getServiceUpdate();
      if (serviceUpdate.length > 0) {
        this.serviceUpdate.message = serviceUpdate[0]['meta_box']['service_update']['phila_service_update_message'];
        this.serviceUpdate.color = serviceUpdate[0]['meta_box']['service_update']['phila_update_level'] == 0 ? "green" : "red";
        this.serviceUpdate.icon = serviceUpdate[0]['meta_box']['service_update']['phila_update_level'] == 0 ? "fa-solid fa-circle-check" : "fa-solid fa-circle-exclamation";
        if (serviceUpdate[0]['meta_box']['service_update']['phila_update_link']) {
          this.serviceUpdate.link = serviceUpdate[0]['meta_box']['service_update']['phila_update_link'];
          if (serviceUpdate[0]['meta_box']['service_update']['phila_update_link_text']) {
            this.serviceUpdate.link_text = serviceUpdate[0]['meta_box']['service_update']['phila_update_link_text'];
          }
        }
        this.loading = false;
      } else {
        this.serviceUpdate.message = "City offices are open for regular business hours. Some services are being offered online or by appointment. To learn more,";
        this.serviceUpdate.link = "https://www.phila.gov/departments";
        this.serviceUpdate.link_text = "check with your department of interest.";
        this.serviceUpdate.color = "green";
        this.serviceUpdate.icon = "fa-solid fa-circle-check";
        this.loading = false;
      }
    },
  },
};
</script>